/**
 * Locale URL Helper
 * Manages URL prefixes for internationalization (/es/, /en/, /fr/, /it/, /pt/)
 */

// URL codes mapping (short URL code -> internal locale code)
export const URL_LOCALE_MAP = {
  'es': 'es',
  'en': 'en-US',
  'fr': 'fr-FR',
  'it': 'it-IT',
  'pt': 'pt-PT'
};

// Reverse mapping (internal locale code -> short URL code)
export const LOCALE_URL_MAP = {
  'es': 'es',
  'en-US': 'en',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'pt-PT': 'pt'
};

// Valid URL language prefixes
export const VALID_LANG_PREFIXES = ['es', 'en', 'fr', 'it', 'pt'];

// Default language
export const DEFAULT_LANG = 'es';
export const DEFAULT_LOCALE = 'es';

/**
 * Convert internal locale to URL prefix
 * @param {string} locale - Internal locale (e.g., 'en-US')
 * @returns {string} URL prefix (e.g., 'en')
 */
export function localeToUrlPrefix(locale) {
  return LOCALE_URL_MAP[locale] || DEFAULT_LANG;
}

/**
 * Convert URL prefix to internal locale
 * @param {string} urlPrefix - URL prefix (e.g., 'en')
 * @returns {string} Internal locale (e.g., 'en-US')
 */
export function urlPrefixToLocale(urlPrefix) {
  return URL_LOCALE_MAP[urlPrefix] || DEFAULT_LOCALE;
}

/**
 * Check if a path segment is a valid language prefix
 * @param {string} segment - Path segment to check
 * @returns {boolean}
 */
export function isValidLangPrefix(segment) {
  return VALID_LANG_PREFIXES.includes(segment);
}

/**
 * Extract language prefix and remaining path from URL
 * @param {string} path - Full URL path (e.g., '/en/boats/123')
 * @returns {{ lang: string, path: string }} - { lang: 'en', path: '/boats/123' }
 */
export function extractLangFromPath(path) {
  if (!path || path === '/') {
    return { lang: null, path: '/' };
  }

  const segments = path.split('/').filter(Boolean);
  
  if (segments.length > 0 && isValidLangPrefix(segments[0])) {
    const lang = segments[0];
    const remainingPath = '/' + segments.slice(1).join('/') || '/';
    return { lang, path: remainingPath };
  }

  return { lang: null, path };
}

/**
 * Build a localized URL path
 * @param {string} path - Original path (e.g., '/boats/123')
 * @param {string} locale - Internal locale (e.g., 'en-US')
 * @returns {string} Localized path (e.g., '/en/boats/123')
 */
export function localePath(path, locale) {
  const urlPrefix = localeToUrlPrefix(locale);
  
  // Remove any existing language prefix
  const { path: cleanPath } = extractLangFromPath(path);
  
  // Build new path with language prefix
  if (cleanPath === '/') {
    return `/${urlPrefix}/`;
  }
  
  return `/${urlPrefix}${cleanPath}`;
}

/**
 * Get all alternate URLs for hreflang tags
 * @param {string} basePath - Path without language prefix (e.g., '/boats/123')
 * @param {string} baseUrl - Base URL (e.g., 'https://www.navegaapp.com')
 * @returns {Array} Array of { lang, url } objects
 */
export function getAlternateUrls(basePath, baseUrl) {
  return VALID_LANG_PREFIXES.map(lang => ({
    lang,
    hreflang: lang === 'es' ? 'es' : lang === 'en' ? 'en' : lang === 'fr' ? 'fr' : lang === 'it' ? 'it' : 'pt',
    url: `${baseUrl}/${lang}${basePath === '/' ? '/' : basePath}`
  }));
}
